import { RefreshRule } from '@pubstack/common/src/adm/rules'
import { FC, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useArchiveRefreshRule, useRefreshRules, useRefreshRulesWithHistory } from '~/api/adm/refresh-rules.api'
import { useScopePermissions } from '~/auth/useScopePermissions'
import { Colors } from '~/assets/style/colors'
import { ActionButtons, ActionsTableCell } from '~/components/_styled/table'
import Button from '~/components/Button'
import { HistoryFlyout } from '~/components/HistoryFlyout'
import { useGlobalFlyout } from '~/components/layout/GlobalFlyout'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { Pagination } from '~/components/Pagination'
import { Tooltip } from '~/components/Tooltip'
import Table, { handleTableSearch, handleTableSort, onColumnSort, SortableColumn, TableColumns } from '~/components/table/Table'
import TableCell from '~/components/table/TableCell'
import TableRow from '~/components/table/TableRow'
import { ArchiveRuleModal } from '~/feature-components/ArchiveRuleModal'
import { RuleListToolbar } from '~/feature-components/RuleListToolbar'
import { useClientPagination } from '~/hooks/useClientPagination'
import { countStacksUse } from '~/modules/adm/rules/countStacksUsed'
import { PureUsedStackTooltip } from '~/modules/adm/rules/PureUsedStackTooltip'
import { RefreshWrapper } from '~/modules/adm/rules/refresh/PureAdmRefreshPage'
import { RuleDetailsFlyout } from '~/modules/adm/rules/RuleDetailsFlyout'

type RefreshRuleListed = Omit<RefreshRule, 'creationTime' | 'technicalId'>

/** Built once, not during render: `new Date()` is impure and the row's date is never shown. */
const NO_RULE_ROW: RefreshRuleListed = { id: 'NO_RULE', name: 'No refresh', timer: 0, capping: 0, updatedBy: 'Pubstack', updatedAt: new Date() }

export const AdmRefreshRulesPage: FC = () => {
  const navigate = useNavigate()

  const { data: refreshRulesHistory, isFetching } = useRefreshRulesWithHistory()
  const { data: refreshRules } = useRefreshRules(true)
  const { mutateAsync: archiveRefreshRule, isPending: isArchiving } = useArchiveRefreshRule({
    successToast: 'Refresh rule archived',
    errorToast: 'Could not archive this refresh rule',
  })
  /* Creating and archiving a rule are owner-only at the API — a rule is shared, so both are decisions
     about every stack that could reach for it. Reading the list stays open. */
  const { canCreate, canArchive, disabledReason } = useScopePermissions()

  const [columns, setColumns] = useState<TableColumns<RefreshRuleListed>>([
    {
      name: 'Rule',
      isSortable: true,
      order: 'none',
      attributeSort: 'name',
    },
    {
      name: 'Viewability timer',
      isSortable: true,
      order: 'none',
      attributeSort: 'timer',
    },
    {
      name: 'Capping',
      isSortable: true,
      order: 'none',
      attributeSort: 'capping',
    },
    {
      name: 'Used on',
      isSortable: true,
      order: 'ascending',
      attributeSort: 'stacksUse',
    },
    {
      name: 'Actions',
      isSortable: false,
    },
  ])

  const flyout = useGlobalFlyout()
  const modal = useGlobalModal()
  const [search, setSearch] = useState('')
  const refreshRulesWithNoRule: RefreshRuleListed[] = [...(refreshRules || []), NO_RULE_ROW]
  const searchedRefreshRules = handleTableSearch(refreshRulesWithNoRule, search, ['name'])
  const sortedColumn = (columns.find((c) => c.isSortable && c.order !== 'none') || columns[0]) as SortableColumn<RefreshRule>
  let displayRefreshRules: RefreshRuleListed[] = []
  if (sortedColumn.attributeSort === 'stacksUse') {
    displayRefreshRules = searchedRefreshRules.sort((a, b) => (sortedColumn.order === 'ascending' ? 1 : -1) * (countStacksUse(b.stacksUse) - countStacksUse(a.stacksUse)))
  } else {
    displayRefreshRules = handleTableSort(columns, searchedRefreshRules)
  }

  const { pageItems, paginationProps, resetPage } = useClientPagination(displayRefreshRules)

  /**
   * Why this row cannot be archived, or `undefined` when it can.
   *
   * The same refusals the API makes, said before the click rather than after it. A rule with no
   * `scopeId` is one of Pubstack's globals, shared by every scope — the same test that already decides
   * whether the pen is enabled.
   */
  const archiveDisabledReason = (refreshRule: RefreshRuleListed): string | undefined => {
    if (!refreshRule.scopeId) {
      return "Can't archive a Pubstack rule"
    }
    const stackCount = countStacksUse(refreshRule.stacksUse)
    if (stackCount > 0) {
      return `Used on ${stackCount} stack${stackCount === 1 ? '' : 's'}. Remove it from them first.`
    }
    return canArchive ? undefined : disabledReason
  }

  /** As on the lazy loading list: `NO_RULE` has no settings to print, so the flyout says what it means instead. */
  const openDetails = (refreshRule: RefreshRuleListed) =>
    flyout.open(
      RuleDetailsFlyout,
      { color: Colors.Lilac, width: '816px', closeTrackingLabel: 'adm-refresh-rules-close' },
      {
        name: refreshRule.name,
        details:
          refreshRule.id === 'NO_RULE'
            ? [{ label: 'Refresh', value: 'Off — ad units on a stack using this rule are not refreshed.' }]
            : [
                { label: 'Timer', value: `${refreshRule.timer}s` },
                { label: 'Capping', value: refreshRule.capping ? `${refreshRule.capping} impressions` : 'No capping' },
              ],
        stacksUse: refreshRule.stacksUse,
        onEdit: refreshRule.scopeId
          ? () => {
              flyout.close()
              navigate(`/monetization/one/refresh/edit/${refreshRule.id}`)
            }
          : undefined,
        editDisabledReason: 'This is a Pubstack rule, so it cannot be edited.',
        editTrackingLabel: 'adm-refresh-rules-edit',
      }
    )

  return (
    <RefreshWrapper>
      {/* The sentence that used to sit here — "Set the refresh rules usable on stacks." — is the page's
          subtitle now, under the title with the other three lists', rather than a line of body text
          below the tabs. */}
      <RuleListToolbar
        search={search}
        onSearchChange={(next) => {
          setSearch(next)
          resetPage()
        }}
        onOpenHistory={
          refreshRulesHistory
            ? () => flyout.open(HistoryFlyout, { color: Colors.King, width: '816px', closeTrackingLabel: 'adm-refresh-rule-history-close' }, { item: 'Refresh rules', data: refreshRulesHistory })
            : undefined
        }
        historyTrackingLabel={'adm-refresh-rule-history-view'}
        createLabel={'New rule'}
        onCreate={() => navigate(`/monetization/one/refresh/new`)}
        createTrackingLabel={'adm-refresh-rules-new'}
        canCreate={canCreate}
        createDisabledReason={disabledReason}
        canSeeArchived={canArchive}
        archivedLabel={'Archived rules'}
        onNavigateToArchived={() => navigate('/monetization/one/refresh/archived')}
        archivedTrackingLabel={'adm-refresh-rules-archived-navigate'}
        actionsTrackingLabel={'adm-refresh-rules-actions-view'}
      />

      <Table columns={columns} isLoading={isFetching} onClickHeading={(column) => onColumnSort(columns, column, setColumns)}>
        {pageItems.map((refreshRule) => (
          /* Every row opens the details, the Pubstack-owned "No refresh" one included: reading a rule is not
             editing it, so the row that could not be clicked at all now has something to show. */
          <TableRow key={refreshRule.id} clickable={{ onClick: () => openDetails(refreshRule), trackingLabel: 'adm-refresh-rules-view' }}>
            <TableCell>{refreshRule.name}</TableCell>
            <TableCell>{refreshRule.id !== 'NO_RULE' ? `${refreshRule.timer}s` : '-'}</TableCell>
            <TableCell>{refreshRule.id !== 'NO_RULE' ? (refreshRule.capping ? `${refreshRule.capping} impressions` : 'No capping') : '-'}</TableCell>
            <TableCell>{refreshRule.id === 'NO_RULE' ? '-' : refreshRule.stacksUse ? <PureUsedStackTooltip stacksBySite={refreshRule.stacksUse} /> : <span>0 stack</span>}</TableCell>
            <ActionsTableCell>
              <ActionButtons>
                <Tooltip title={'View'} positions={['left', 'bottom', 'top', 'right']}>
                  <Button
                    iconName={'view'}
                    variant={'tertiary'}
                    title={'View'}
                    onClick={(event) => {
                      event.preventDefault()
                      event.stopPropagation()
                      openDetails(refreshRule)
                    }}
                    trackingLabel={'adm-refresh-rules-icon-view'}
                  />
                </Tooltip>
                <Tooltip title={!refreshRule.scopeId ? `Can't edit a Pubstack rule` : 'Edit'} positions={['left', 'bottom', 'top', 'right']}>
                  <Button
                    iconName={'edit'}
                    variant={'tertiary'}
                    title={'Edit'}
                    onClick={(event) => {
                      event.preventDefault()
                      event.stopPropagation()
                      navigate(`/monetization/one/refresh/edit/${refreshRule.id}`)
                    }}
                    disabled={!refreshRule.scopeId}
                    trackingLabel={'adm-refresh-rules-icon-edit'}
                  />
                </Tooltip>
                {/* Owners only, and only for a rule nothing uses — the button stays on the row saying
                    which of the two it is, rather than disappearing and leaving the reader to guess
                    whether archiving exists at all. */}
                {canArchive && (
                  <Tooltip title={archiveDisabledReason(refreshRule) ?? 'Archive'} positions={['left', 'bottom', 'top', 'right']}>
                    <Button
                      iconName={'archive'}
                      variant={'tertiary'}
                      title={'Archive'}
                      disabled={!!archiveDisabledReason(refreshRule) || isArchiving}
                      onClick={(event) => {
                        event.preventDefault()
                        event.stopPropagation()
                        modal.open(ArchiveRuleModal, {
                          name: refreshRule.name,
                          noun: 'refresh rule',
                          onArchive: () => archiveRefreshRule({ id: refreshRule.id, archived: true }),
                          isPending: isArchiving,
                          trackingPrefix: 'adm-refresh-rules-archive',
                        })
                      }}
                      trackingLabel={'adm-refresh-rules-archive'}
                    />
                  </Tooltip>
                )}
              </ActionButtons>
            </ActionsTableCell>
          </TableRow>
        ))}
      </Table>
      <Pagination {...paginationProps} className={'mt-4'} />
    </RefreshWrapper>
  )
}
